import React, { useState, useEffect } from 'react';
import { api } from '../../api/nocode';
import { sendEmail } from '../../api/ses';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

export default function LettersQueue() {
  const [letters, setLetters] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [lRes, uRes] = await Promise.all([
        api.get('self_letters_fbwm'),
        api.get('users_gwacamp')
      ]);
      setLetters(Array.isArray(lRes) ? lRes : lRes.data || []);
      setUsers(Array.isArray(uRes) ? uRes : uRes.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const deliverLetter = async (letter) => {
    const user = users.find(u => u.id === letter.user_id);
    if (!user) return false;

    const subject = `Your Day ${letter.day} Camp Letter`;
    const htmlBody = `
      <div style="font-family: 'Helvetica Neue', sans-serif; max-width: 600px; margin: 0 auto; color: #1A2E0A; background: #FDF6EC; padding: 40px 30px; border-radius: 8px;">
        <h2 style="color: #2D5016; margin-bottom: 20px;">GospelWise Author Camp - Day ${letter.day}</h2>
        <div style="font-size: 18px; line-height: 1.8; white-space: pre-wrap; font-style: italic; background: white; padding: 30px; border: 1px solid #EDE0CC; border-radius: 4px; color: #1A2E0A;">
          ${letter.letter_body}
        </div>
        <p style="margin-top: 30px; font-size: 14px; color: #6B6B6B;">
          This letter was written by you, for you, before camp began.
        </p>
      </div>
    `;

    const success = await sendEmail(user.email, subject, htmlBody);
    if (success) {
      await api.put('self_letters_fbwm', letter.id, { 
        status: 'delivered',
        sent_at: new Date().toISOString()
      });
      return true;
    }
    return false;
  };

  const deliverAllScheduled = async () => {
    if (!window.confirm("Are you sure you want to deliver all currently scheduled letters?")) return;
    setSending(true);
    const scheduled = letters.filter(l => l.status === 'scheduled');
    let count = 0;
    for (const letter of scheduled) {
      const success = await deliverLetter(letter);
      if (success) count++;
    }
    setSending(false);
    alert(`Successfully delivered ${count} letters.`);
    loadData();
  };

  const formatEST = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateStr;
    }
  };

  const scheduledLetters = letters.filter(l => l.status === 'scheduled');
  const deliveredLetters = letters.filter(l => l.status === 'delivered');
  const draftLetters = letters.filter(l => l.status === 'draft');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-md shadow-sm border border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon name="Mail" /> Letter Delivery Queue
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage and manually trigger scheduled camper letters.</p>
        </div>
        <Button onClick={deliverAllScheduled} disabled={sending || scheduledLetters.length === 0} className="bg-gwa-blue hover:bg-blue-700">
          <SafeIcon name="Send" /> Deliver All Scheduled Now
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="text-center p-6 border-l-4 border-amber">
          <div className="text-sm text-gray-500 uppercase font-bold mb-2">Total Drafts</div>
          <div className="text-4xl font-bold text-amber">{draftLetters.length}</div>
        </Card>
        <Card className="text-center p-6 border-l-4 border-forest-green">
          <div className="text-sm text-gray-500 uppercase font-bold mb-2">Scheduled for Delivery</div>
          <div className="text-4xl font-bold text-forest-green">{scheduledLetters.length}</div>
        </Card>
        <Card className="text-center p-6 border-l-4 border-gwa-blue">
          <div className="text-sm text-gray-500 uppercase font-bold mb-2">Successfully Delivered</div>
          <div className="text-4xl font-bold text-gwa-blue">{deliveredLetters.length}</div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-bold text-gray-800">Scheduled Queue</h2>
        </div>
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Camper</th>
              <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Schedule Info</th>
              <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-400 font-medium">Loading queue data...</td></tr>
            ) : scheduledLetters.length === 0 ? (
              <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-400 font-medium">No letters currently scheduled for delivery.</td></tr>
            ) : scheduledLetters.map(letter => {
              const user = users.find(u => u.id === letter.user_id);
              return (
                <tr key={letter.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-bold text-gray-900">{user?.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-500">{user?.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-700">
                    <div>Day {letter.day}</div>
                    <div className="text-xs text-gray-500 mt-1">Target: {formatEST(letter.scheduled_send_at)}</div>
                    {letter.test_scheduled_time && (
                      <div className="text-xs text-amber font-bold mt-1 bg-amber/10 inline-block px-2 py-0.5 rounded">
                        Test: {formatEST(letter.test_scheduled_time)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-100 text-green-700">Scheduled</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button 
                      variant="outline" 
                      className="text-xs py-1.5 border-forest-green text-forest-green hover:bg-forest-green hover:text-white" 
                      onClick={async () => {
                        setSending(true);
                        await deliverLetter(letter);
                        setSending(false);
                        loadData();
                      }}
                      disabled={sending}
                    >
                      Deliver Now
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}